import {StrictMode, useEffect, useMemo} from 'react';
import {view, RootLayer} from '@sail/ui';
import {Provider as SailClassicProvider} from '@sail/theme-classic';
import {IntlProvider} from 'react-intl';
import {MessageFormatElement} from 'intl-messageformat-parser';
import {captureException} from '@sentry/browser';

import {
  AppConfig,
  AppProfile,
  ErrorBoundaryFallback,
  StripeAssistant,
} from '@stripe-internal/conversations-assistant';
import {
  MobileAssistant,
  MobileErrorBoundaryFallback,
} from '@stripe-internal/mobile-conversations-assistant';

import {DefaultInlineDataComponentRegistry} from '@stripe-internal/mse-inline-data';
import {Config} from '@stripe-internal/conversations-shared-dependencies';
import {
  ObservabilityProvider,
  useObservability,
} from './Providers/ObservabilityProvider';
import {ErrorBoundary} from './components/ErrorBoundary';
import {supportFetch} from './helpers/supportFetch';

function buildConfig(
  config: Config,
  locale = 'en-US',
): {config: AppConfig; profile: AppProfile} {
  const {
    entrypoint: entryPoint,
    visitor,
    user,
    merchant,
    featureFlags,
    experiments,
    supportPlan,
    livemode,
  } = config;

  const profile: AppProfile = {
    visitor,
    user,
    merchant,
    featureFlags,
    experiments,
    supportPlan,
    livemode,
  };

  const appConfig: AppConfig = {
    ...config,
    locale,
    entryPoint,
    initialVisibility: 'closed',
  };

  return {config: appConfig, profile};
}

type Props = {
  locale?: string;
  messages?: Record<string, string> | Record<string, MessageFormatElement[]>;
  config: Config;
};

function AppByEntryPoint({
  config,
  profile,
}: {
  config: AppConfig;
  profile: AppProfile;
}) {
  const observability = useObservability();

  useEffect(() => {
    if (!profile.featureFlags.useExtractedConversationsAssistant) {
      captureException('Running extracted app without feature flag enabled', {
        extra: {
          entryPoint: config.entryPoint,
        },
      });
    }
  }, [config, profile]);

  const mobileEntryPoints = [
    'mobile_ios',
    'mobile_android',
    'react_native_mobile_ios',
    'react_native_mobile_android',
    'mobile_link_ios',
    'mobile_link_android',
  ];

  if (mobileEntryPoints.includes(config.entryPoint)) {
    return (
      <ErrorBoundary fallback={<MobileErrorBoundaryFallback config={config} />}>
        <MobileAssistant
          config={config}
          profile={profile}
          observability={observability}
        />
      </ErrorBoundary>
    );
  }

  return (
    <SailClassicProvider>
      <RootLayer>
        <view.div css={{height: '100vh'}}>
          <ErrorBoundary fallback={<ErrorBoundaryFallback config={config} />}>
            <StripeAssistant
              config={config}
              profile={profile}
              clientFetch={supportFetch}
              inlineDataComponents={DefaultInlineDataComponentRegistry}
              observability={observability}
              withLiveChat
            />
          </ErrorBoundary>
        </view.div>
      </RootLayer>
    </SailClassicProvider>
  );
}

export function ExtractedApp({locale = 'en-US', messages, config}: Props) {
  const {config: appConfig, profile} = useMemo(() => {
    return buildConfig(config, locale);
  }, [config, locale]);

  return (
    <IntlProvider locale={locale} messages={messages} onError={() => {}}>
      <StrictMode>
        <ObservabilityProvider>
          <AppByEntryPoint config={appConfig} profile={profile} />
        </ObservabilityProvider>
      </StrictMode>
    </IntlProvider>
  );
}
